import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CubeTransparentIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { assembliesApi } from "../../api/assemblies";
import Button from "../../components/ui/Button";
import { Card, CardBody } from "../../components/ui/Card";
import Modal from "../../components/ui/Modal";
import Input from "../../components/ui/Input";

export default function AssemblyListPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "Assembly", description: "", rpm: "", design_flow: "", design_head: "" });
  const [err, setErr] = useState(null);

  useEffect(() => {
    assembliesApi.list(projectId)
      .then(r => {
        if (r.data.length > 0) {
          // One assembly per project — go straight to it
          navigate(`/projects/${projectId}/assemblies/${r.data[0].id}`, { replace: true });
        } else {
          setLoading(false);
        }
      })
      .catch(() => { setErr("Failed to load assembly"); setLoading(false); });
  }, [projectId, navigate]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const r = await assembliesApi.create(projectId, {
        name: form.name,
        description: form.description || undefined,
        rpm: form.rpm ? parseFloat(form.rpm) : undefined,
        design_flow: form.design_flow ? parseFloat(form.design_flow) : undefined,
        design_head: form.design_head ? parseFloat(form.design_head) : undefined,
      });
      navigate(`/projects/${projectId}/assemblies/${r.data.id}`);
    } catch (e) {
      setErr(e.response?.data?.error || "Failed to create assembly");
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-400">Loading…</div>;

  return (
    <div className="p-6 max-w-lg space-y-6">
      <button onClick={() => navigate(`/projects/${projectId}`)}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
        <ArrowLeftIcon className="h-4 w-4" />
        Back to project
      </button>
      <div className="flex items-center gap-3">
        <CubeTransparentIcon className="h-8 w-8 text-gray-400" />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Assembly</h1>
          <p className="text-gray-500 text-sm">One assembly per project — groups blade rows for combined analysis.</p>
        </div>
      </div>

      {err && <p className="text-red-500 text-sm">{err}</p>}

      <Card>
        <CardBody className="text-center py-12">
          <p className="text-gray-500 mb-6">No assembly created yet for this project.</p>
          <Button icon={CubeTransparentIcon} onClick={() => setShowCreate(true)}>
            Create Assembly
          </Button>
        </CardBody>
      </Card>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Assembly">
        <div className="space-y-3">
          <Input label="Name" value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <Input label="Description" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          <div className="grid grid-cols-3 gap-3">
            <Input label="RPM" unit="rpm" value={form.rpm}
              onChange={e => setForm(f => ({ ...f, rpm: e.target.value }))} />
            <Input label="Flow" unit="m³/s" value={form.design_flow}
              onChange={e => setForm(f => ({ ...f, design_flow: e.target.value }))} />
            <Input label="Head" unit="m" value={form.design_head}
              onChange={e => setForm(f => ({ ...f, design_head: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} loading={creating} disabled={!form.name}>Create</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
